import { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { FaSpinner, FaExclamationTriangle, FaFileExport } from "react-icons/fa";
import DataTable from "./DataTable";
import DateFormat from "../CustomerPortal/DateFormat";
import { formatSalesValue } from "./../../utils/formatUtils";
import { CustomModal } from "../Modal/CustomModal";

const checkNan = (sales) => (sales || sales === 0 ? formatSalesValue(sales) : "");

const CustomerCreditSection = ({
    header,
    similarCustomer,
    similarCustomerLoading,
    similarCustomerError,
    handleDetails,
    token,
    selectedOutlet,
    url,
    fetchSelectedData,
    showCustomerDetails,
    creditDetails,
    creditWiseBillList,
    creditWisePaymentList,
    detailsLoading,
    detailsError,
    showBills,
    toggleBills,
    showPayments,
    togglePayments,
    selectedCustomer,
    onMakePayment,
}) => {
    const [panModal, setPanModal] = useState({ open: false, guestName: "", guestID: "", panNo: "" });
    const [savingPan, setSavingPan] = useState(false);

    const billRows = Array.isArray(creditWiseBillList) ? creditWiseBillList : [];
    const paymentRows = Array.isArray(creditWisePaymentList) ? creditWisePaymentList : [];
    const billTotal = billRows.reduce((sum, item) => sum + parseFloat(item.Total || 0), 0);
    const paymentTotal = paymentRows.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);

    const openPanModal = (info) => {
        setPanModal({ open: true, guestName: info.GuestName, guestID: info.guestID, panNo: "" });
    };

    const closePanModal = () => setPanModal((prev) => ({ ...prev, open: false }));

    const convertDate = (date) => {
        if (!date) return "";

        return new Date(date).toLocaleDateString("en-CA");
    };

    const handleAddPan = async () => {
        if (!panModal.panNo) {
            toast.error("PAN number cannot be empty");
            return;
        }

        setSavingPan(true);
        try {
            await axios.post(`${url}/update_guest_pan`, {
                guest_id: panModal.guestID,
                guest_name: panModal.guestName,
                guest_pan: panModal.panNo,
                outlet: selectedOutlet,
                token,
            });
            toast.success("PAN updated successfully");
            fetchSelectedData();
            closePanModal();
        } catch (error) {
            console.error(error);
            toast.error("Failed to update PAN");
        } finally {
            setSavingPan(false);
        }
    };

    const exportBillsToExcel = () => {
        const sheetData = [
            ["Customer", selectedCustomer || "All"],
            [],
            ["Bill", "Date", "Discount Amount", "Total"],
        ];

        billRows.forEach((info) => {
            sheetData.push([
                info.bill_no,
                convertDate(info.Date),
                info.DiscountAmt,
                info.Total,
            ]);
        });

        sheetData.push(["Total", "", "", billTotal.toFixed(3)]);

        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
        worksheet["!cols"] = [{ wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Total Credit Sales");

        const customerName = (selectedCustomer || "customer").replace(/\s+/g, "_");
        XLSX.writeFile(workbook, `total-credit-sales-${customerName}.xlsx`);
    };

    return (
        <div className="space-y-4">
            {/* Matching customers */}
            {similarCustomerLoading ? (
                <div className="flex items-center justify-center gap-2 text-gray-600 py-4">
                    <FaSpinner className="animate-spin" />
                    Loading customers...
                </div>
            ) : similarCustomerError ? (
                <div className="flex items-center justify-center gap-2 text-red-600 py-4">
                    <FaExclamationTriangle />
                    {similarCustomerError}
                </div>
            ) : (
                <DataTable
                    columns={[
                        { key: "guestID", label: header[0] },
                        { key: "GuestName", label: header[1] },
                        { key: "guestEmail", label: header[2] },
                        { key: "guestAddress", label: header[3] },
                        { key: "guestPhone", label: header[4] },
                        {
                            key: "guest_pan",
                            label: header[5],
                            render: (info) =>
                                info.guest_pan ? (
                                    info.guest_pan
                                ) : (
                                    <span
                                        className="italic text-blue-600 cursor-pointer hover:underline"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openPanModal(info);
                                        }}
                                    >
                                        Add Pan No.?
                                    </span>
                                ),
                        },
                    ]}
                    rows={similarCustomer}
                    rowKey={(info) => info.guestID}
                    onRowClick={(info) => handleDetails(info.guestID)}
                    emptyMessage="No matching customers"
                    maxH
                />
            )}

            {/* Credit details */}
            {showCustomerDetails && (
                <div className="space-y-3">
                    {detailsLoading ? (
                        <div className="flex items-center justify-center gap-2 text-gray-600 py-6">
                            <FaSpinner className="animate-spin text-lg" />
                            Loading credit details...
                        </div>
                    ) : detailsError ? (
                        <div className="flex items-center justify-center gap-2 text-red-600 py-6">
                            <FaExclamationTriangle />
                            {detailsError}
                        </div>
                    ) : (
                        <>
                            <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 space-y-2.5">
                                <div className="flex items-center justify-between gap-3 font-semibold">
                                    <div className="text-gray-600 text-sm">
                                        Total Credit Sale:{" "}
                                        <span className="text-gray-800">
                                            {checkNan(creditDetails.TotalCreditSale)}
                                        </span>
                                    </div>
                                    <button
                                        onClick={toggleBills}
                                        className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                                    >
                                        {showBills ? "Hide" : "View"}
                                    </button>
                                </div>

                                <div className="flex items-center justify-between gap-3 font-semibold">
                                    <div className="text-gray-600 text-sm">
                                        Total Amount Paid:{" "}
                                        <span className="text-gray-800">
                                            {checkNan(creditDetails.TotalPaid)}
                                        </span>
                                    </div>
                                    <button
                                        onClick={togglePayments}
                                        className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                                    >
                                        {showPayments ? "Hide" : "View"}
                                    </button>
                                </div>

                                <hr className="border-gray-500" />

                                <p className="text-gray-800 font-semibold text-sm">
                                    Remaining Balance: {checkNan(creditDetails.RemainingAmount)}
                                </p>
                            </div>

                            <div className="flex justify-center">
                                <button
                                    onClick={onMakePayment}
                                    className="px-4 py-2 rounded-md font-medium bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md text-sm transition"
                                >
                                    Make Payment
                                </button>
                            </div>

                            {showBills && (
                                <div className="space-y-2">
                                    <div className="flex justify-end">
                                        <button
                                            onClick={exportBillsToExcel}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md transition"
                                        >
                                            <FaFileExport size={12} />
                                            Export
                                        </button>
                                    </div>
                                    <DataTable
                                        columns={[
                                            { key: "bill_no", label: "Bill" },
                                            { key: "Date", label: "Date", render: (r) => <DateFormat date={r.Date} /> },
                                            { key: "DiscountAmt", label: "Discount Amount" },
                                            { key: "Total", label: "Total" },
                                        ]}
                                        rows={billRows}
                                        rowKey={(r, i) => r.bill_no || i}
                                        totalRow={["Total:", "", "", billTotal.toFixed(3)]}
                                        maxH
                                    />
                                </div>
                            )}

                            {showPayments && !creditWisePaymentList?.error && (
                                <DataTable
                                    columns={[
                                        { key: "Date", label: "Date", render: (r) => <DateFormat date={r.Date} /> },
                                        { key: "Time", label: "Time" },
                                        { key: "Amount", label: "Amount" },
                                        { key: "PaymentMode", label: "Payment Mode" },
                                    ]}
                                    rows={paymentRows}
                                    rowKey={(r, i) => i}
                                    totalRow={["Total:", "", paymentTotal.toFixed(3), ""]}
                                />
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Add PAN modal */}
            <CustomModal show={panModal.open} onHide={closePanModal} size="md" centered>
                <CustomModal.Header closeButton onHide={closePanModal}>
                    <CustomModal.Title>Add Pan No</CustomModal.Title>
                </CustomModal.Header>
                <CustomModal.Body>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                            <input
                                type="text"
                                disabled
                                value={panModal.guestName}
                                className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Pan No</label>
                            <input
                                type="text"
                                value={panModal.panNo}
                                onChange={(e) => setPanModal((prev) => ({ ...prev, panNo: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </CustomModal.Body>
                <CustomModal.Footer className="flex justify-end gap-2">
                    <button
                        onClick={closePanModal}
                        className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
                    >
                        Close
                    </button>
                    <button
                        onClick={handleAddPan}
                        disabled={savingPan}
                        className="px-4 py-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition"
                    >
                        {savingPan ? "Saving..." : "Add Pan"}
                    </button>
                </CustomModal.Footer>
            </CustomModal>
        </div>
    );
};

export default CustomerCreditSection;